import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import LayerDriftSeverityBadge from "@/v2-backoffice-client/layer/drift/LayerDriftSeverityBadge"
import {
  asCountSeverity,
  formatCountDelta,
} from "@/v2-backoffice-client/layer/drift/layer.drift.utils"
import {
  formatRelativeTime,
  getProviderName,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import type { LayerDriftSummaryFragment } from "@/v2-backoffice-client/uptime/dashboard/__types__/dashboard.driftSummaries.fragment"
import type { UptimeInlineFragment } from "@/v2-backoffice-client/uptime/inline/__types__/uptime.inline.fragment"

export type UptimeDashboardDriftRowProps = ComponentProps<"div"> & {
  target: UptimeInlineFragment
  // Optional severity rollup matched from the drift summaries
  // query. When present the row paints the severity badges and the
  // "N changes" total; when absent it falls back to a generic
  // "metadata changed" label.
  summary: LayerDriftSummaryFragment | null
}

// One row in the "Configuration drift" lane. Surfaces the per-target
// severity rollup so the lane reads "panic stuff first" at a glance.
function UptimeDashboardDriftRow(props: UptimeDashboardDriftRowProps) {
  const { target, summary, className, ...divProps } = props

  const provider = getProviderName(target)
  const countDrift = summary?.CountDrift ?? null
  const countSeverity = countDrift?.HasCountDrift
    ? asCountSeverity(countDrift.Severity)
    : null
  const countDelta = countDrift?.HasCountDrift
    ? formatCountDelta(countDrift.BaselineCount, countDrift.CurrentCount)
    : null
  // Pick the most recent of metadata + count drift detection so the
  // "drifted X ago" tail reflects whichever signal is currently
  // making the row show up in this lane.
  const detected =
    pickLatest(target.DriftDetectedAt, target.CountDriftDetectedAt) ?? null
  const total =
    summary?.EntryCount ??
    (summary
      ? summary.BreakingCount + summary.WarningCount + summary.InfoCount
      : 0)

  return (
    <div
      className={cn(
        "uptime-dashboard-row",
        "uptime-dashboard-row-drift",
        className,
      )}
      {...divProps}
    >
      <div className="uptime-dashboard-row-main">
        <div className="uptime-dashboard-row-name">
          {target.Name ?? target.Identifier}
        </div>
        <div className="uptime-dashboard-row-reason">
          {provider}
          {target.Protocol ? ` · ${target.Protocol}` : ""}
          {total > 0 && (
            <span className="uptime-dashboard-row-aside">
              {" "}
              · {total} change{total === 1 ? "" : "s"}
            </span>
          )}
          {countDrift?.HasCountDrift && countDelta && (
            <span className="uptime-dashboard-row-aside">
              {" "}
              · count {countDelta.pct}
            </span>
          )}
        </div>
        {(summary?.HasDrift || countSeverity) && (
          <div className="uptime-dashboard-row-severity">
            {summary && summary.BreakingCount > 0 && (
              <LayerDriftSeverityBadge severity="breaking">
                {summary.BreakingCount} breaking
              </LayerDriftSeverityBadge>
            )}
            {summary && summary.WarningCount > 0 && (
              <LayerDriftSeverityBadge severity="warning">
                {summary.WarningCount} warning
              </LayerDriftSeverityBadge>
            )}
            {summary && summary.InfoCount > 0 && (
              <LayerDriftSeverityBadge severity="info">
                {summary.InfoCount} info
              </LayerDriftSeverityBadge>
            )}
            {countSeverity && countSeverity !== "none" && (
              <LayerDriftSeverityBadge severity={countSeverity}>
                count {countSeverity}
              </LayerDriftSeverityBadge>
            )}
          </div>
        )}
      </div>
      <div className="uptime-dashboard-row-aside uptime-dashboard-row-drift-aside">
        {detected
          ? `drifted ${formatRelativeTime(detected)}`
          : "drift detected"}
      </div>
    </div>
  )
}

// pickLatest returns whichever ISO timestamp parses to the larger
// epoch-ms value. Used so the drift lane's "drifted X ago" tail
// always reflects the most recent of metadata vs count detection.
function pickLatest(
  a: string | null | undefined,
  b: string | null | undefined,
): string | null {
  const ams = a ? new Date(a).getTime() : 0
  const bms = b ? new Date(b).getTime() : 0
  if (ams === 0 && bms === 0) return null
  return bms > ams ? (b ?? null) : (a ?? null)
}

export default UptimeDashboardDriftRow
